import { FC } from "react";
import { Input } from "antd";
import { useTranslation } from "react-i18next";
import { RoomIcon } from "src/assets/Icons";
import PulseLoadingIndicator from "src/components/PulseLoadingIndicator/PulseLoadingIndicator";
import { POPULAR_HOTELS } from "../constants";
import HotelIcon from "@Icons/Hotels/HotelsIcon";
import NoData from "@Components/NoData/NoData";

const HotelSelectOptions: FC<any> = ({ options = [], onChange, hasError, hasSearch, isFetching }) => {
  const { t } = useTranslation();
  const showPopular = isFetching || !hasSearch || (options.length === 0 && !hasError);
  const title = showPopular ? t("Hotels.search.popularLocations") : t("Hotels.search.searchResults");
  const displayOptions = showPopular ? POPULAR_HOTELS : options;

  if (!isFetching && hasError && hasSearch)
    return (
      <div className="flex items-center justify-center p-5 w-full h-full">
        <NoData description={t("Hotels.search.noHotelsOrCitiesFound")} />
      </div>
    );

  if (!displayOptions.length) return null;

  return (
    <div className="flex w-full flex-col gap-1 px-2">
      {title && <div className="font-semibold text-font-subtle">{title}</div>}
      <div className="flex flex-col divide-border-subtle divide-y">
        {displayOptions.map((option: any) => {
          const isHotelOption = option.type === "hotels";
          return (
            <div
              key={`${option.type}-${option.value}`}
              className="w-full! flex items-center gap-1 hover:text-font-active py-2"
              onClick={() => onChange?.(option)}
            >
              <div className="flex h-1 w-1 flex-1 items-center justify-center font-bold text-font-active">
                {isHotelOption ? <RoomIcon className="h-4 w-4" /> : <HotelIcon className="h-4 w-4" />}
              </div>
              <div className="flex-9 flex flex-col items-start justify-center text-font-default hover:text-font-default">
                <span className="whitespace-break-spaces font-semibold">{option.label}</span>
                <span className="text-font-minimal">{option.subLabel}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const HotelSelectMob: FC<any> = ({
  searchValue,
  isFetching,
  onSearch,
  onChange,
  options = [],
  icon: Icon,
  error,
  validationError = [],
}) => {
  const hasValidationErrors = validationError.length >= 1;

  return (
    <div className="flex flex-col gap-2">
      <div className="outline-b outline-border-hover shadow-border-subtle sticky top-0 z-10 w-full bg-background-default p-4 shadow-sm">
        <div className="gap-2! outline-border-default focus-within:outline-border-active flex shrink-0 items-center justify-center rounded-md px-4 outline">
          {Icon && (
            <div>
              <Icon className="h-5 w-5 text-current" />
            </div>
          )}
          <Input
            autoFocus
            value={searchValue}
            suffix={<PulseLoadingIndicator show={isFetching} running={isFetching} className="bg-background-primary-lighter!" />}
            onChange={(e) => {
              onSearch?.(e.target.value);
            }}
            variant="borderless"
            className="p-4!"
          />
        </div>
      </div>
      <div className="px-3">{hasValidationErrors && <div className="flex items-center justify-start text-font-danger">{validationError?.[0]}</div>}</div>

      <div className="flex w-full flex-col px-3 py-1">
        <HotelSelectOptions options={options} onChange={onChange} hasError={error} hasSearch={searchValue} isFetching={isFetching} />
      </div>
    </div>
  );
};

export default HotelSelectMob;
